import { Navigate } from 'react-router-dom';
import { useAuth } from '@/features/identity-management/auth/hooks/useAuth';
import type { ActorType } from '@/app/layouts/app-nav';
import type { CurrentUser } from '@/features/identity-management/auth/models/auth.types';
import { Button, PageStatus } from '@/shared/ui/primitives';
import { useT } from '@/shared/i18n';
import { ROUTES } from './route-paths';
import type { ReactNode } from 'react';

/**
 * True when the user is allowed a workspace. Only the `recruiter` ("partner")
 * bucket is denied for now — every other actor, INCLUDING a not-yet-onboarded
 * (`null`) account, defaults to the candidate workspace. (A null actor was
 * previously restricted, which wrongly locked out real candidates whose
 * account had not been stamped with an actor type yet.)
 */
function hasWorkspaceAccess(user: CurrentUser | null): boolean {
  if (!user) return false;
  return user.primaryActorType !== 'recruiter';
}

interface GuardProps {
  children: ReactNode;
}

/**
 * The post-auth home for a user by actor: admins → admin dashboard, employers →
 * company workspace, candidates (and unknown actor types) → the workspace home.
 * Single source of truth for both the login redirect and the `/app` index guard.
 */
export function homeRouteForUser(user: CurrentUser | null): string {
  if (user?.permissions.includes('metrics.read') || user?.primaryActorType === 'admin') {
    return ROUTES.app.admin;
  }
  if (user?.primaryActorType === 'employer') {
    return ROUTES.app.employer;
  }
  return ROUTES.app.root;
}

export function RequireAuth({ children }: GuardProps): ReactNode {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return <PageStatus state="loading" />;
  }
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.auth.login} replace />;
  }
  return children;
}

export function PublicOnly({ children }: GuardProps): ReactNode {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return <PageStatus state="loading" />;
  }
  if (isAuthenticated) {
    return <Navigate to={ROUTES.app.root} replace />;
  }
  return children;
}

/**
 * Restricts a route to users whose primary actor type is in `allowed`.
 * Assumes an outer `RequireAuth`; redirects others to the app root.
 */
export function RequireActorTypes({
  allowed,
  children,
}: GuardProps & { allowed: ActorType[] }): ReactNode {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return <PageStatus state="loading" />;
  }
  const actorType = user?.primaryActorType ?? null;
  if (!actorType || !allowed.includes(actorType)) {
    return <Navigate to={ROUTES.app.root} replace />;
  }
  return children;
}

/**
 * The workspace home (`/app`) is candidate-only. Admins and employers are
 * redirected to their own home so they never land on the candidate workspace.
 */
export function WorkspaceHomeRoute({ children }: GuardProps): ReactNode {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return <PageStatus state="loading" />;
  }
  const home = homeRouteForUser(user);
  if (home !== ROUTES.app.root) {
    return <Navigate to={home} replace />;
  }
  return children;
}

/**
 * Standalone restricted screen for actor types without a workspace (recruiter
 * and unknown / "partner"). Renders no nav and no product data — just a clear
 * message and a sign-out — so nothing leaks to an unsupported actor.
 */
function RestrictedAccessScreen(): ReactNode {
  const { t } = useT();
  const { logout } = useAuth();
  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-[var(--surface-base)] px-6 text-center text-[var(--text-primary)]">
      <h1 className="text-2xl font-semibold tracking-tight">
        {t('appShell.restricted.title')}
      </h1>
      <p className="max-w-md text-sm text-[var(--text-muted)]">
        {t('appShell.restricted.body')}
      </p>
      <Button variant="secondary" onClick={() => void logout()}>
        {t('appShell.userMenu.logout')}
      </Button>
    </div>
  );
}

/**
 * Gates the entire authenticated shell to actor types that have a workspace.
 * Assumes an outer `RequireAuth`. Denied actors get the restricted screen.
 */
export function RequireWorkspaceAccess({ children }: GuardProps): ReactNode {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return <PageStatus state="loading" />;
  }
  if (!hasWorkspaceAccess(user)) {
    return <RestrictedAccessScreen />;
  }
  return children;
}

/**
 * Restricts a route to users holding a specific permission. A denied user gets
 * an in-shell "access denied" surface (keeps the workspace chrome + nav) rather
 * than a silent redirect, so the denial is explainable.
 */
export function RequirePermission({
  permission,
  children,
}: GuardProps & { permission: string }): ReactNode {
  const { t } = useT();
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return <PageStatus state="loading" />;
  }
  if (!user?.permissions.includes(permission)) {
    return (
      <PageStatus
        state="error"
        title={t('router.unauthorized.title')}
        body={t('router.unauthorized.body')}
      />
    );
  }
  return children;
}
